import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  bffApi,
  ParticipationRequestStatus,
  useUpdateParticipationRequestMutation,
  type ParticipationRequestDto,
  type ParticipationRequestPatchDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import {
  coordinatorRegistry,
  useApiErrorEditorConfig,
  useCreateEditor,
} from "../../../../common/editing/save-coordinator";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { usePortalEventUrls } from "../usePortalEventUrls";
import { saveWithVersionConflictRetry } from "../../buyer/wizard/saveWithVersionConflictRetry";
import { PortalLayout } from "../components/PortalLayout";
import { PortalWizardShell } from "../../../../common/wizard/PortalWizardShell";
import { usePortalStepLabel, usePortalStepShortLabel } from "../../../../common/wizard/usePortalStepLabel";
import { useWizardStepNavigation } from "../../../../common/wizard/useWizardStepNavigation";
import { BidIntroStep } from "../bid/steps/BidIntroStep";
import { ExclusionStep } from "../bid/steps/ExclusionStep";
import { SupplierInfoStep } from "../bid/steps/SupplierInfoStep";
import { RequestQualificationStep } from "./RequestQualificationStep";
import { RequestReviewStep } from "./RequestReviewStep";
import { requestWizardModel } from "./requestWizardSteps";

interface Props {
  eventId: string;
  requestId: string;
  request: ParticipationRequestDto;
  event: PortalSourcingEventDetailDto;
}

export const EditParticipationRequestForm = ({ eventId, requestId, request, event }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const portalUrls = usePortalEventUrls();
  const editorErrorConfig = useApiErrorEditorConfig();
  const [updateRequest] = useUpdateParticipationRequestMutation();
  const stepLabel = usePortalStepLabel();
  const stepShortLabel = usePortalStepShortLabel();
  const { currentStep, navigateToStep, navPropsFor } = useWizardStepNavigation({
    model: requestWizardModel,
    visibility: undefined,
    stepPaths: portalUrls.request.edit.go(eventId, requestId),
    stepLabel,
    beforeNavigate: async () => {
      await coordinatorRegistry.flush(requestId);
      return coordinatorRegistry.get(requestId)?.getEntityStatus() !== "error";
    },
  });
  const readOnly = request.status !== ParticipationRequestStatus.Draft;

  const config = useMemo(
    () => ({
      initialState: request,
      save: async (fields: Partial<ParticipationRequestPatchDto>) =>
        await saveWithVersionConflictRetry({
          fields,
          save: async (retryFields) =>
            await updateRequest({
              requestId,
              participationRequestPatchDto: retryFields as ParticipationRequestPatchDto,
            }).unwrap(),
          reloadLatest: async () => {
            await dispatch(
              bffApi.endpoints.getParticipationRequest.initiate({ requestId }, { forceRefetch: true }),
            ).unwrap();
          },
        }),
      ...editorErrorConfig,
    }),
    [request, requestId, dispatch, updateRequest, editorErrorConfig],
  );

  useCreateEditor<ParticipationRequestDto, ParticipationRequestPatchDto>(requestId, config);

  if (!currentStep) return <PortalLayout>{t("Loading")}</PortalLayout>;

  const renderStep = (): ReactNode => {
    switch (currentStep) {
      case "intro":
        return <BidIntroStep event={event} {...navPropsFor("intro")} />;
      case "supplier":
        return (
          <SupplierInfoStep
            entityId={requestId}
            event={event}
            readOnly={readOnly}
            {...navPropsFor("supplier")}
          />
        );
      case "exclusion":
        return (
          <ExclusionStep
            entityId={requestId}
            event={event}
            readOnly={readOnly}
            {...navPropsFor("exclusion")}
          />
        );
      case "qualification":
        return (
          <RequestQualificationStep
            requestId={requestId}
            event={event}
            readOnly={readOnly}
            {...navPropsFor("qualification")}
          />
        );
      case "review":
        return (
          <RequestReviewStep
            eventId={eventId}
            requestId={requestId}
            request={request}
            event={event}
            previousStep={navPropsFor("review").previousStep}
          />
        );
    }
  };

  return (
    <PortalLayout>
      <PortalWizardShell
        backTo={portalUrls.view.go(eventId)}
        backLabel={t("Back to the procurement")}
        title={t("Request to participate")}
        subtitle={event.title}
        steps={requestWizardModel.visibleSteps(undefined)}
        currentStep={currentStep}
        stepLabel={stepLabel}
        stepShortLabel={stepShortLabel}
        navigateToStep={navigateToStep}
      >
        {renderStep()}
      </PortalWizardShell>
    </PortalLayout>
  );
};
